// Catalog of immersive "job simulation" experiences - enterprise-skinned projects
// where the learner steps into a role at a fictional company and does the real
// work (tickets, docs, QA) instead of clicking through a vanilla tutorial.
//
// The catalog is DB-backed: it lists every seeded Simulation. Adding a new one is
// a seed, no client change.
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { FiArrowRight, FiClock } from "react-icons/fi";
import { getCatalog } from "../../features/simulation/simulationSlice";

function SimCard({ sim }) {
  const company = sim.company || {};
  const companyName = company.name || sim.name;
  const mark = (company.short || companyName || "?").slice(0, 1);
  const title = company.projectName || sim.name;

  return (
    <Link to={`/simulations/${sim.slug}`} className="block h-full">
      <div className="h-full flex flex-col bg-zinc-800/70 border border-zinc-700 hover:border-purple-400/60 rounded-2xl p-6 transition-colors">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-11 h-11 rounded-xl grid place-items-center text-white font-extrabold text-lg bg-gradient-to-br from-blue-500 to-blue-700">
            {mark}
          </span>
          <div>
            <div className="text-sm text-zinc-400">{companyName}</div>
            <div className="text-xs text-purple-300 font-medium">{sim.role}</div>
          </div>
        </div>
        <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
        <p className="text-sm text-zinc-300 leading-relaxed flex-grow">{sim.blurb}</p>
        <div className="flex flex-wrap gap-2 my-4">
          {(sim.tags || []).map((t) => (
            <span
              key={t}
              className="text-xs font-medium text-zinc-300 bg-zinc-700/70 rounded-full px-2.5 py-1"
            >
              {t}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2 text-purple-300 font-semibold text-sm">
          Enter the workspace <FiArrowRight />
        </div>
      </div>
    </Link>
  );
}

function ComingSoonCard() {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center bg-zinc-800/30 border border-dashed border-zinc-700 rounded-2xl p-6 min-h-[220px]">
      <FiClock className="w-7 h-7 text-zinc-500 mb-3" />
      <div className="text-zinc-300 font-semibold">More simulations coming soon</div>
      <p className="text-xs text-zinc-500 mt-1 max-w-xs">
        New roles at new companies - admin, developer, and architect tracks.
      </p>
    </div>
  );
}

function SimulationList() {
  const dispatch = useDispatch();
  const { catalog, isLoadingCatalog } = useSelector((s) => s.simulation);

  useEffect(() => {
    dispatch(getCatalog());
  }, [dispatch]);

  return (
    <div className="min-h-screen bg-zinc-900 cursor-default">
      <Helmet>
        <title>Job Simulations | Camp Apex</title>
        <meta
          name="description"
          content="Immersive, enterprise-style job simulations. Step into a role at a fictional company and do the real work - tickets, docs and all."
        />
      </Helmet>

      <div className="max-w-5xl mx-auto px-6 pt-16 pb-24">
        <div className="text-center mb-12">
          <span className="inline-block text-xs font-bold uppercase tracking-wider text-purple-300 bg-purple-500/10 border border-purple-500/30 rounded-full px-3 py-1 mb-4">
            New
          </span>
          <h1 className="text-3xl lg:text-4xl font-bold text-white mb-3">Job Simulations</h1>
          <p className="text-zinc-400 max-w-2xl mx-auto">
            Step into a role at a fictional company and do the actual work - pick up tickets off a
            board, read the internal wiki, ship against a real API. Immersive, enterprise-style
            projects that go well beyond a vanilla tutorial.
          </p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          {(catalog || []).map((sim) => (
            <SimCard key={sim.slug} sim={sim} />
          ))}
          {!isLoadingCatalog && <ComingSoonCard />}
        </div>
      </div>
    </div>
  );
}

export default SimulationList;
